import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import AuthOmhulsel, { Etiket } from '../components/AuthOmhulsel';
import { Foutmelding } from '../components/ui';
import { useAuth } from '../lib/auth';

export default function Aanmelden() {
  const { signUp } = useAuth();
  const [naam, setNaam] = useState('');
  const [gebruikersnaam, setGebruikersnaam] = useState('');
  const [email, setEmail] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [bevestigMail, setBevestigMail] = useState(false);

  async function verstuur(e: FormEvent) {
    e.preventDefault();
    if (!naam.trim() || !email || !wachtwoord || !gebruikersnaam.trim()) {
      setFout('Vul je naam, gebruikersnaam, e-mailadres en een wachtwoord in.');
      return;
    }
    if (!/^[a-z0-9][a-z0-9._-]{2,19}$/.test(gebruikersnaam.trim().toLowerCase())) {
      setFout('Een gebruikersnaam is 3 tot 20 tekens: kleine letters, cijfers, punt, streepje of liggend streepje.');
      return;
    }
    if (wachtwoord.length < 6) {
      setFout('Kies een wachtwoord van minstens zes tekens.');
      return;
    }

    setBezig(true);
    setFout(null);
    try {
      const { bevestigingNodig } = await signUp(
        email.trim(),
        wachtwoord,
        naam.trim(),
        gebruikersnaam.trim().toLowerCase()
      );
      if (bevestigingNodig) setBevestigMail(true);
      /* Anders is er meteen een sessie en neemt App.tsx het over met het
         wachtscherm — daar hoeft hier niets voor te gebeuren. */
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Aanmelden mislukte.');
    } finally {
      setBezig(false);
    }
  }

  if (bevestigMail) {
    return (
      <AuthOmhulsel>
        <h1 className="font-display text-[32px] leading-tight">Bevestig je e-mailadres</h1>
        <p className="mt-3 text-sm leading-relaxed text-krijt-600">
          We hebben een mail gestuurd naar <strong>{email}</strong>. Klik op de link erin; daarna kan een beheerder je
          account vrijgeven.
        </p>
        <Link to="/inloggen" className="mt-6 inline-block text-sm font-medium text-veld-800 underline underline-offset-2">
          Terug naar inloggen
        </Link>
      </AuthOmhulsel>
    );
  }

  return (
    <AuthOmhulsel>
      <h1 className="font-display text-[32px] leading-tight">Account aanmaken</h1>
      <p className="mt-1 text-sm text-krijt-600">Een beheerder geeft je daarna toegang tot de competitie.</p>

      <form onSubmit={verstuur} className="mt-7">
        {fout ? <Foutmelding>{fout}</Foutmelding> : null}

        <label className="mb-4 block">
          <Etiket tekst="Naam" verplicht />
          <input className="veld" value={naam} autoComplete="name" onChange={(e) => setNaam(e.target.value)} />
        </label>

        <label className="mb-4 block">
          <Etiket tekst="Gebruikersnaam" verplicht />
          <div className="flex items-center gap-2">
            <span className="text-krijt-400">@</span>
            <input
              className="veld"
              value={gebruikersnaam}
              autoCapitalize="off"
              spellCheck={false}
              placeholder="bijv. imre"
              onChange={(e) => setGebruikersnaam(e.target.value)}
            />
          </div>
          <span className="mt-1 block text-xs text-krijt-500">
            Hiermee nodigen anderen je uit voor een poule. Moet uniek zijn.
          </span>
        </label>

        <label className="mb-4 block">
          <Etiket tekst="E-mailadres" verplicht />
          <input
            type="email"
            className="veld"
            value={email}
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="mb-5 block">
          <Etiket tekst="Wachtwoord" verplicht />
          <input
            type="password"
            className="veld"
            value={wachtwoord}
            autoComplete="new-password"
            onChange={(e) => setWachtwoord(e.target.value)}
          />
          <span className="mt-1 block text-xs text-krijt-500">Minstens zes tekens.</span>
        </label>

        <button
          type="submit"
          disabled={bezig}
          className="w-full rounded-full bg-veld-800 py-2.5 text-sm font-semibold text-white transition hover:bg-veld-900 disabled:opacity-50"
        >
          {bezig ? 'Bezig…' : 'Account aanmaken'}
        </button>

        <p className="mt-5 text-sm text-krijt-600">
          Heb je al een account?{' '}
          <Link to="/inloggen" className="font-medium text-veld-800 underline underline-offset-2">
            Inloggen
          </Link>
        </p>
      </form>
    </AuthOmhulsel>
  );
}
